/**
 * GroupProxyPage — /assess/group-proxy (VER-115, Group Model B).
 *
 * Landing surface for practitioner proxy reads: the history of reads recorded on groups' behalf,
 * plus the form to record a new one.
 *
 * Reads GET /api/survey-responses/proxy, which returns GROUP-level reads only — a proxy read tied
 * to a named participant is that person's own score and is deliberately absent from this list.
 * The server also scopes Facilitators to the groups they facilitate.
 *
 * The VER-57 shell rendered mock submissions and badged them against hardcoded WEMWBS bands
 * (< 41 low, ≤ 52 moderate). Those thresholds belong to a specific licensed instrument and were
 * wrong for any other survey, so the table now shows the score the template's own scoring produced
 * and names the survey it came from, rather than interpreting it against one scale's cut-points.
 */

import React, { useCallback, useEffect, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Divider,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import { AddOutlined, EditNoteOutlined, GroupsOutlined } from "@mui/icons-material";
import { useMobile } from "../../../hooks/useMobile";
import { useAuth } from "../../../contexts/auth.context";
import { GroupProxyRead, listGroupProxyReads } from "../../../client/api/survey";
import Spinner from "../../layout/Spinner";
import { GroupProxyForm } from "./GroupProxyForm";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatDate = (iso: string): string =>
    new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

/** finalScore where the template configures one, else the raw total. Mirrors the reporting rule. */
const displayScore = (read: GroupProxyRead): string => {
    const score = read.finalScore ?? read.rawScore;
    return score === null ? "—" : String(score);
};

const AdministrationChip = ({ administration }: { administration: string }): JSX.Element => (
    <Chip
        size="small"
        label={administration}
        color={administration === "Baseline" ? "info" : administration === "Endpoint" ? "success" : "default"}
        variant="outlined"
    />
);

// ---------------------------------------------------------------------------
// Model B header — amber accent, deliberately distinct from Model A teal
// ---------------------------------------------------------------------------

const ModelBHeader = ({ onNewEntry, canRecord }: { onNewEntry: () => void; canRecord: boolean }): JSX.Element => (
    <Box
        sx={{
            p: 3,
            mb: 3,
            borderRadius: (theme) => `${theme.shape.borderRadius}px`,
            backgroundColor: (theme) => theme.palette.warning.light,
            border: "1px solid",
            borderColor: "warning.main",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
        }}
    >
        <Box display="flex" alignItems="center" gap={1.5}>
            <GroupsOutlined sx={{ fontSize: "2rem", color: "warning.dark" }} />
            <Box>
                <Typography variant="h5" color="warning.dark">
                    Model B — Group proxy read
                </Typography>
                <Typography variant="body2" color="warning.dark" sx={{ opacity: 0.85, mt: 0.25 }}>
                    No participant devices. The facilitator records the survey on behalf of the group.
                </Typography>
            </Box>
        </Box>
        {/* Viewing the history and recording a read are separate rights (VER-115). The server
            enforces it; hiding the button keeps a view-only user from being offered a 403. */}
        {canRecord ? (
            <Button
                variant="contained"
                color="warning"
                startIcon={<AddOutlined />}
                onClick={onNewEntry}
                sx={{ flexShrink: 0 }}
            >
                Record proxy read
            </Button>
        ) : null}
    </Box>
);

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export const GroupProxyPage = (): JSX.Element => {
    const { isMobile, isTablet } = useMobile();
    const { hasPermission } = useAuth();
    const padding = !isTablet && isMobile ? 2 : 4;
    const canRecord = hasPermission("SurveyProxyRespond");

    const [showForm, setShowForm] = useState(false);
    const [reads, setReads] = useState<GroupProxyRead[]>([]);
    // The endpoint caps the page. Held so the list can say it is a page, not the whole history.
    const [withheld, setWithheld] = useState(0);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [savedAlert, setSavedAlert] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setLoadError(null);
        try {
            const page = await listGroupProxyReads();
            setReads(page.items);
            setWithheld(page.truncated ? Math.max(page.total - page.items.length, 0) : 0);
        } catch (error: unknown) {
            setLoadError(error instanceof Error ? error.message : "Proxy reads could not be loaded.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        void load();
    }, [load]);

    /**
     * Re-fetch rather than appending the submitted values locally. The persisted row carries the
     * server's scoring, timestamp and recording user; showing a locally-assembled row would display
     * something the database does not hold.
     */
    const handleFormSuccess = async () => {
        setShowForm(false);
        setSavedAlert(true);
        await load();
    };

    return (
        <Box p={padding} width={1}>
            {!isTablet && isMobile ? <Box pt={8} /> : null}

            <Box mb={3} display="flex" alignItems="center" gap={1}>
                <EditNoteOutlined color="action" sx={{ fontSize: "2rem" }} />
                <Typography variant="h2" color="textPrimary">
                    Group assessment
                </Typography>
            </Box>

            {savedAlert ? (
                <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSavedAlert(false)}>
                    Proxy read saved.
                </Alert>
            ) : null}

            <ModelBHeader onNewEntry={() => setShowForm(true)} canRecord={canRecord} />

            {showForm && canRecord ? (
                <Card sx={{ mb: 4 }}>
                    <CardContent>
                        <Typography variant="h5" mb={3}>
                            New proxy read
                        </Typography>
                        <Divider sx={{ mb: 3 }} />
                        <GroupProxyForm onSubmitSuccess={handleFormSuccess} onCancel={() => setShowForm(false)} />
                    </CardContent>
                </Card>
            ) : null}

            <Box>
                <Typography variant="h5" color="textPrimary" mb={2}>
                    Previous proxy reads
                </Typography>

                {loading ? (
                    <Spinner />
                ) : loadError ? (
                    <Alert
                        severity="error"
                        action={
                            <Button color="inherit" size="small" onClick={() => void load()}>
                                Retry
                            </Button>
                        }
                    >
                        {loadError}
                    </Alert>
                ) : reads.length === 0 ? (
                    <Card>
                        <CardContent>
                            <Typography variant="body1" color="textSecondary" align="center">
                                No proxy reads recorded yet. Use &quot;Record proxy read&quot; above to add the first
                                entry.
                            </Typography>
                        </CardContent>
                    </Card>
                ) : (
                    <Card>
                        {isMobile && !isTablet ? (
                            <CardContent>
                                <Grid container spacing={2}>
                                    {reads.map((read) => (
                                        <Grid item xs={12} key={read.id}>
                                            <Box
                                                sx={{
                                                    p: 2,
                                                    border: "1px solid",
                                                    borderColor: "borderShade.main",
                                                    borderRadius: (theme) => `${theme.shape.borderRadius}px`,
                                                }}
                                            >
                                                <Box
                                                    display="flex"
                                                    justifyContent="space-between"
                                                    alignItems="flex-start"
                                                    mb={1}
                                                >
                                                    <Typography variant="body2" fontWeight={600}>
                                                        {formatDate(read.recordedAt)}
                                                    </Typography>
                                                    <Typography variant="body2" fontWeight={600}>
                                                        {displayScore(read)}
                                                    </Typography>
                                                </Box>
                                                <Typography variant="body2" color="textSecondary">
                                                    {read.programmeName ?? "—"}
                                                    {read.sessionNumber !== null
                                                        ? ` — Session ${read.sessionNumber}`
                                                        : ""}
                                                </Typography>
                                                <Typography variant="body2" color="textSecondary">
                                                    {read.templateTitle ?? "—"}
                                                </Typography>
                                                <Box mt={1}>
                                                    <AdministrationChip administration={read.administration} />
                                                </Box>
                                                {read.recordedBy ? (
                                                    <Typography variant="body2" color="textSecondary" mt={1}>
                                                        Recorded by {read.recordedBy}
                                                    </Typography>
                                                ) : null}
                                            </Box>
                                        </Grid>
                                    ))}
                                </Grid>
                            </CardContent>
                        ) : (
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Date recorded</TableCell>
                                        <TableCell>Group</TableCell>
                                        <TableCell>Session</TableCell>
                                        <TableCell>Survey</TableCell>
                                        <TableCell>Administration</TableCell>
                                        <TableCell>Score</TableCell>
                                        <TableCell>Recorded by</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {reads.map((read) => (
                                        <TableRow key={read.id}>
                                            <TableCell>
                                                <Typography variant="body2">{formatDate(read.recordedAt)}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">{read.programmeName ?? "—"}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {read.sessionNumber !== null ? read.sessionNumber : "—"}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">{read.templateTitle ?? "—"}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <AdministrationChip administration={read.administration} />
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">{displayScore(read)}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" color="textSecondary">
                                                    {read.recordedBy ?? "—"}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </Card>
                )}

                {/*
                 * Said out loud because the alternative is a page that reads as a complete history.
                 * A coordinator counting proxy reads to judge how much of a programme was
                 * practitioner-observed would otherwise undercount without any cue that they had
                 * only been shown the newest ones.
                 */}
                {withheld > 0 ? (
                    <Typography variant="body2" color="textSecondary" mt={2}>
                        Showing the {reads.length} most recent reads. {withheld} older{" "}
                        {withheld === 1 ? "read is" : "reads are"} not shown.
                    </Typography>
                ) : null}
            </Box>
        </Box>
    );
};
